<template>

    <div>
        <!-- Reorderable mode with custom row structure -->
        <div v-if="reorderable">
            <div class="table table-sm mb-0 hide-header">
                <Draggable v-model="localValue" 
                          :disabled="editingRowIndex >= 0"
                          handle=".drag-handle"
                          @end="onDragEnd"
                          tag="div"
                          class="table-body">
                    <div v-for="(item, index) in localValue" :key="index" 
                         class="table-row d-flex align-items-center">
                        <!-- Handle column -->
                        <div class="table-cell drag-handle-column">
                            <div class="drag-handle" :class="{ 'text-muted': editingRowIndex >= 0 }" style="cursor: move;">
                                <i class="fas fa-grip-vertical"></i>
                            </div>
                        </div>
                        
                        <!-- Value column -->
                        <div class="table-cell flex-fill">
                            <b-input v-model="editingRowData" v-if="editingRowIndex === index" :value="item"
                                :maxlength="maxlength" autofocus />
                            <span v-else>{{ item }}</span>
                        </div>
                        
                        <!-- Actions column -->
                        <div class="table-cell actions">
                            <div class="d-flex" style="gap: 6px;">
                                <template v-if="editingRowIndex !== index">
                                    <b-button variant="link" @click.prevent="startTableEdit({ value: item }, index)" title="Edit">
                                        <b-icon icon="pencil" variant="primary" />
                                    </b-button>
                                    <b-button variant="link" @click.prevent="removeTableItem(index)" title="Remove">
                                        <b-icon icon="x-circle-fill" variant="danger" />
                                    </b-button>
                                </template>
                                <template v-else>
                                    <b-button variant="link" title="Save" @click.prevent="saveTableEdit(index)">
                                        <b-icon icon="save" variant="primary" />
                                    </b-button>
                                    <b-button variant="link" title="Cancel Edit" @click.prevent="cancelTableEdit">
                                        <b-icon icon="x-lg" variant="" />
                                    </b-button>
                                </template>
                            </div>
                        </div>
                    </div>
                </Draggable>
            </div>
        </div>
        
        <!-- Non-reorderable mode with b-table -->
        <div v-else>
            <b-table :items="tableItems" :fields="tableFields"
                class="mb-0 hide-header" small bordered>
                <template #cell(value)="{ item, index }">
                    <b-input v-model="editingRowData" v-if="editingRowIndex === index" :value="item.value"
                        :maxlength="maxlength" autofocus />
                    <template v-else>{{ item.value }}</template>
                </template>
                <template #cell(actions)="{ item, index }">
                    <div class="d-flex" style="gap: 6px;">
                        <template v-if="editingRowIndex !== index">
                            <b-button variant="link" @click.prevent="startTableEdit(item, index)" title="Edit">
                                <b-icon icon="pencil" variant="primary" />
                            </b-button>
                            <b-button variant="link" @click.prevent="removeTableItem(index)" title="Remove">
                                <b-icon icon="x-circle-fill" variant="danger" />
                            </b-button>
                        </template>
                        <template v-else>
                            <b-button variant="link" title="Save" @click.prevent="saveTableEdit(index)">
                                <b-icon icon="save" variant="primary" />
                            </b-button>
                            <b-button variant="link" title="Cancel Edit" @click.prevent="cancelTableEdit">
                                <b-icon icon="x-lg" variant="" />
                            </b-button>
                        </template>
                    </div>
                </template>
            </b-table>
        </div>

        <b-button size="sm" variant="primary" @click="addTableItem">
            {{ addLabel ?? "Add" }}
        </b-button>
    </div>

</template>

<script lang="ts">
import { Vue, Prop, Component, Watch } from "vue-property-decorator";
import Draggable from 'vuedraggable';

@Component({
    name: "StringArrayField",
    components: {
        Draggable
    }
})
export default class StringArrayField extends Vue {
    @Prop({ default: () => [] }) value!: string[];
    @Prop({ type: String, default: 'Add' }) addLabel!: string;
    @Prop({ default: null }) maxlength!: number | null;
    @Prop({ type: Boolean, default: false }) reorderable!: boolean;

    // Local component data with proper types
    editingRowData: string | undefined = undefined;
    editingRowIndex: number = -1;
    localValue: string[] = [];

    @Watch('value', { immediate: true, deep: true })
    onValueChange(newValue: string[]) {
        this.localValue = [...newValue];
    }

    get tableItems() {
        return this.localValue.map((ir, ix) => ({ index: ix, value: ir }));
    }

    get tableFields() {
        const fields: any[] = [];
        
        // Only include handle column for b-table in non-reorderable mode
        // In reorderable mode, we use custom div structure
        
        fields.push(
            { key: 'value', label: '' },
            { key: 'actions', label: '', class: 'actions' }
        );
        
        return fields;
    }

    onDragEnd() {
        this.$emit('input', [...this.localValue]);
    }

    startTableEdit(item: { value: string }, index: number): void {
        this.editingRowIndex = index;
        this.editingRowData = item.value;
    }

    async addTableItem() {
        //If currently editing, commit the edit first
        if (this.editingRowIndex >= 0) {
            this.saveTableEdit(this.editingRowIndex);
            await this.$nextTick();
        }

        const cleanedArray = this.localValue.filter((a) => a.trim()); // Trimmed array to remove empty values
        cleanedArray.push(""); // Push a new empty string to the array

        // Update local value and emit
        this.localValue = cleanedArray;
        this.$emit("input", [...this.localValue]);
        this.startTableEdit({ value: "" }, cleanedArray.length - 1);
    }

    removeTableItem(index: number): void {
        const array = [...this.localValue];
        array.splice(index, 1); // Remove the element at the given index
        this.localValue = array;
        this.$emit("input", [...this.localValue]); // Emit the updated array
    }

    cancelTableEdit(): void {
        const editingIndex = this.editingRowIndex;
        const editingValue = this.localValue[editingIndex];

        // If the value being edited is empty, remove it
        if (!editingValue) {
            this.removeTableItem(editingIndex);
        }

        // Reset editing state
        this.editingRowIndex = -1;
        this.editingRowData = undefined;
    }

    saveTableEdit(index: number): void {
        const array = [...this.localValue];
        const newValue = this.editingRowData?.trim();

        // Update the value or remove the item based on the trimmed value
        if (!newValue) {
            array.splice(index, 1);
        } else {
            array.splice(index, 1, newValue);
        }

        // Update local value and emit
        this.localValue = array;
        this.$emit("input", [...this.localValue]);
        this.editingRowIndex = -1;
        this.editingRowData = undefined;
    }
}
</script>

<style scoped lang="scss">
table.hide-header ::v-deep(thead) {
    display: none;
}

table ::v-deep(.actions) {
    width: 50px;
}

table ::v-deep(.drag-handle-column) {
    width: 30px;
    text-align: center;
}

.drag-handle {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    
    &.text-muted {
        cursor: not-allowed !important;
        opacity: 0.5;
    }
}

// Custom table styles for reorderable mode
.table {
    &.hide-header .table-body .table-row:first-child {
        // Hide header row effect in custom structure
    }
}

.table-body {
    display: block;
}

.table-row {
    border: 1px solid #dee2e6;
    border-top: 0;
    padding: 0.375rem;
    min-height: 38px; // Match b-table-sm row height
    
    &:first-child {
        border-top: 1px solid #dee2e6;
    }
    
    &:hover {
        background-color: rgba(0, 0, 0, 0.075);
    }
}

.table-cell {
    display: flex;
    align-items: center;
    padding: 0 0.5rem;
    
    &.drag-handle-column {
        width: 30px;
        flex-shrink: 0;
        text-align: center;
        padding: 0 0.25rem;
    }
    
    &.actions {
        width: 100px;
        flex-shrink: 0;
        justify-content: flex-end;
    }
    
    &.flex-fill {
        flex: 1;
    }
}
</style>
